import { FormControl, InputLabel, MenuItem, Select, TextField } from '@mui/material';
import { memo, useRef, useState } from 'react';
import { GridColumnConfig } from '../../models/gridCoumnConfig';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import dayjs from 'dayjs';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';

interface EditableCellProps {
  column: GridColumnConfig;
  onEditComplete?: (data: any) => void;
  dataItem?: any;
}

const limitDecimalPlaces = (value: any) => {
  if (typeof value !== 'string') {
    throw new Error('Input must be a string');
  }

  // Find the decimal point
  const decimalIndex = value?.indexOf('.');

  if (decimalIndex === -1) {
    // If there's no decimal point, return the original value
    return value;
  }

  // Get the part after the decimal point
  const decimalPart = value.slice(decimalIndex + 1);

  if (decimalPart.length > 2) {
    // If more than two digits after the decimal point, truncate it
    const truncatedDecimalPart = decimalPart.slice(0, 2);
    return value.slice(0, decimalIndex + 1) + truncatedDecimalPart;
  }

  return value; // If there are 2 or fewer digits after the decimal, return the original value
};

const NormalEditableSell = ({ column, onEditComplete }: EditableCellProps) => {
  return memo((dataItem: any) => {
    const [editableData, setEditableData] = useState(dataItem.dataItem[column.field]);

    const handleOnBlur = () => {
      if (editableData !== dataItem.dataItem[column.field]) {
        onEditComplete?.({ ...dataItem.dataItem, [column.field]: editableData });
      }
    };

    const handleOnChange = (e: any) => {
      const inputValue = e.target.value;
      setEditableData(inputValue);
    };

    const handleEditClick = () => {
      if (editableData === dataItem.dataItem[column.field]) {
        setEditableData(dataItem.dataItem[column.field] ?? '');
      }
    };

    return (
      <td onClick={handleEditClick}>
        {editableData !== undefined ? (
          <TextField
            value={editableData}
            onBlur={handleOnBlur}
            onChange={handleOnChange}
            variant="outlined"
            size="small"
          />
        ) : (
          dataItem.dataItem[column.field] ?? ''
        )}
      </td>
    );
  });
};

const NumericCellEditor = ({ column, onEditComplete }: EditableCellProps) => {
  return memo((dataItem: any) => {
    const [editableData, setEditableData] = useState(dataItem.dataItem[column.field]);

    const handleOnBlur = () => {
      if (editableData !== dataItem.dataItem[column.field]) {
        onEditComplete?.({ ...dataItem.dataItem, [column.field]: editableData });
      }
    };

    const editTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    const handleOnChange = (e: any) => {
      const inputValue = e.target.value;

      let sanitizedValue: string;
      if (!column?.navigate || !column?.isDecimal) {
        sanitizedValue = inputValue;
        // Prevent negative values if column?.navigate is false or undefined
        if (!column?.navigate) {
          // sanitizedValue = Math.max(parseFloat(inputValue), 0).toString();
        }

        if (!column?.isDecimal) {
          sanitizedValue = sanitizedValue?.replace(/\D/g, '');
          sanitizedValue = Math.max(parseInt(sanitizedValue || '0', 10), 0).toString();
        }
      } else {
        sanitizedValue = inputValue;
      }
      if (column?.isDecimal) {
        sanitizedValue = sanitizedValue?.replace(/[^0-9.]/g, '');
        // sanitizedValue = sanitizedValue.replace(/\.00$/, '');
        sanitizedValue = limitDecimalPlaces(sanitizedValue);
      }
      setEditableData(sanitizedValue);
      if (sanitizedValue !== dataItem.dataItem[column.field] && !!column.isDisableByTime) {
        if (editTimeoutRef.current) {
          clearTimeout(editTimeoutRef.current);
        }

        // Set a new timeout
        editTimeoutRef.current = setTimeout(() => {
          onEditComplete?.({ ...dataItem.dataItem, [column.field]: sanitizedValue });
        }, 1400);
      }
    };

    const handleEditClick = () => {
      if (editableData === dataItem.dataItem[column.field]) {
        setEditableData(dataItem.dataItem[column.field] ?? '');
      }
    };

    return (
      <td onClick={handleEditClick}>
        {editableData !== undefined ? (
          <TextField
            value={editableData}
            onBlur={handleOnBlur}
            onChange={handleOnChange}
            variant="outlined"
            size="small"
            type="number"
            disabled={!!column.isDisable}
          />
        ) : (
          dataItem.dataItem[column.field] ?? ''
        )}
      </td>
    );
  });
};

const SelectBoxCell = ({ column, onEditComplete }: EditableCellProps) => {
  return ({ dataItem }: any) => {
    const [editableData, setEditableData] = useState(dataItem[column.field]);

    const handleOnBlur = () => {
      if (editableData !== dataItem[column.field]) {
        onEditComplete?.({ ...dataItem, [column.field]: editableData });
      }
    };

    const handleOnChange = (e: any) => {
      const newValue = e.target.value;
      setEditableData(newValue);
    };

    const isVisible = !dataItem?.isHourlyorTimeMaterialService;

    return (
      <td>
        {!isVisible && (
          <FormControl fullWidth>
            <InputLabel id="demo-simple-select-label"> {column?.title}</InputLabel>
            <Select
              labelId="demo-simple-select-label"
              id="demo-simple-select"
              value={editableData ?? ''}
              onBlur={handleOnBlur}
              onChange={handleOnChange}
              variant="outlined"
              size="small"
            >
              {column?.options?.map((option: any) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </td>
    );
  };
};

const DateCellEditor = ({ column, onEditComplete }: EditableCellProps) => {
  return ({ dataItem }: any) => {
    const [editableData, setEditableData] = useState(dataItem[column.field] ? dayjs(dataItem[column.field]) : null);

    const handleOnChange = (newValue: dayjs.Dayjs | null) => {
      if (newValue && dayjs(newValue).isValid()) {
        // Extract the year and check its validity
        const year = newValue.year();
        if (year >= 1500 && year <= 4000) {
          // Ensure the year is 4 digits
          setEditableData(newValue);

          // Compare the new value with the original value and call `onEditComplete` if it's different
          if (newValue.toISOString() !== dataItem[column.field]) {
            onEditComplete?.({ ...dataItem, [column.field]: newValue.toISOString() });
          }
        }
      }
    };

    return (
      <td>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker value={editableData} onChange={handleOnChange} />
        </LocalizationProvider>
      </td>
    );
  };
};

const limitToHourMinuteFormat = (value: string): string => {
  let [integerPart, decimalPart] = value.split('.');

  // Ensure integerPart is a valid number
  integerPart = integerPart ? Math.max(parseInt(integerPart, 10), 0).toString() : '0';

  // Normalize decimalPart
  if (decimalPart) {
    decimalPart = decimalPart.slice(0, 2); // Limit to two digits

    if (decimalPart?.length === 1) {
      // If only one digit, pad with '0'

      decimalPart = decimalPart.padEnd(2, '0');
    }

    if (parseInt(decimalPart, 10) >= 60) {
      // If decimal part >= 60, increment integer and reset decimal to 00
      integerPart = (parseInt(integerPart, 10) + 1).toString();
      decimalPart = '00';
    } else {
      // If only one digit, normalize to two digits
      decimalPart = decimalPart.padEnd(2, '0');
    }
  } else {
    // If no decimal part, set it to '00'
    decimalPart = '00';
  }
  const finalValue = integerPart === '0' && decimalPart === '00' ? '' : `${integerPart}.${decimalPart}`;

  return finalValue;
};

const NumericCellEditorHours = ({ column, onEditComplete }: EditableCellProps) => {
  return memo((dataItem: any) => {
    const [editableData, setEditableData] = useState(dataItem.dataItem[column.field]);

    const handleOnBlur = () => {
      // Normalize the value using the formatting function
      const normalizedValue = limitToHourMinuteFormat(editableData || '');

      // If value has changed, call onEditComplete with the new value
      if (normalizedValue !== dataItem.dataItem[column.field]) {
        onEditComplete?.({ ...dataItem.dataItem, [column.field]: normalizedValue });
      }

      // Update the editable data with the normalized value
      setEditableData(normalizedValue);
    };

    const handleOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const inputValue = e.target.value;

      // Sanitize input to allow only digits and a single dot
      let sanitizedValue = limitDecimalPlaces(inputValue);
      sanitizedValue = sanitizedValue?.replace(/(\..*?)\..*/g, '$1'); // Allow only one dot
      // sanitizedValue = limitToHourMinuteFormat(sanitizedValue);

      setEditableData(sanitizedValue);
    };

    const handleEditClick = () => {
      if (editableData === dataItem.dataItem[column.field]) {
        setEditableData(dataItem.dataItem[column.field] ?? '');
      }
    };

    return (
      <td onClick={handleEditClick}>
        {editableData !== undefined ? (
          <TextField
            value={editableData}
            onBlur={handleOnBlur}
            onChange={handleOnChange}
            variant="outlined"
            size="small"
            type="text"
            disabled={!!column.isDisable}
          />
        ) : (
          dataItem.dataItem[column.field] ?? ''
        )}
      </td>
    );
  });
};

export const EditableSell = (column: GridColumnConfig, onEditComplete?: (data: any) => void) => {
  if (column.editor === 'numeric') {
    return NumericCellEditor({ column, onEditComplete });
  }

  if (column.editor === 'select') {
    return SelectBoxCell({ column, onEditComplete });
  }

  if (column.editor === 'date') {
    return DateCellEditor({ column, onEditComplete });
  }

  if (column.editor === 'textHour') {
    return NumericCellEditorHours({ column, onEditComplete });
  }

  return NormalEditableSell({ column, onEditComplete });
};
